import React, { useState, useEffect } from 'react'
import {
  Modal,
  Input,
  Button,
  Select,
  Upload,
  message,
  Icon,
  Tooltip,
  Checkbox,
  Spin
} from 'antd'
import { useSelector } from 'react-redux'
import { ImagePreview } from '../../../../../../components'
import useCharacters from '../../../../hooks/useCharacters'

const { TextArea } = Input
const { Option } = Select

const EditCharacterModal = ({
  visible,
  character,
  videoId,
  onClose,
  onUpdated
}) => {
  const normalizeGender = value => {
    if (typeof value !== 'string') return null
    const normalized = value.trim().toLowerCase()
    return normalized || null
  }

  // --- Form state ---
  const [name, setName] = useState('')
  const [gender, setGender] = useState(null)
  const [originalPrompt, setOriginalPrompt] = useState('')
  const [apparel, setApparel] = useState('')
  const [isSystem, setIsSystem] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [activeImageIndex, setActiveImageIndex] = useState(0)

  // --- Image state ---
  const [newImages, setNewImages] = useState([])
  const [replaceImages, setReplaceImages] = useState(false)

  const user = useSelector(state => state.user)
  const isSuperAdmin = user?.info?.is_super_admin || user?.info?.isSuperAdmin

  const { editCharacter } = useCharacters({ videoId })

  // --- Pre-fill form when character changes ---
  useEffect(() => {
    if (!character) return

    const attrs = character.attributes || {}

    setName(character.name || '')
    setGender(normalizeGender(character.gender))
    setOriginalPrompt(attrs.original_prompt || '')
    setIsSystem(character.type === 'system')

    // Parse apparel to display string
    let apparelStr = ''
    if (Array.isArray(attrs.character_apparel)) {
      apparelStr = attrs.character_apparel.join(', ')
    } else if (typeof attrs.character_apparel === 'string') {
      let cleaned = attrs.character_apparel.trim()
      if (cleaned.startsWith('[') && cleaned.endsWith(']')) {
        cleaned = cleaned.substring(1, cleaned.length - 1)
        apparelStr = cleaned
          .split(',')
          .map(s => s.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean)
          .join(', ')
      } else {
        apparelStr = cleaned
      }
    }
    setApparel(apparelStr)

    // Reset image state on new character
    setNewImages([])
    setReplaceImages(false)
    setActiveImageIndex(0)
  }, [character])

  // --- Image upload handler ---
  const handleImageUpload = file => {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp', 'image/gif']
    if (!allowedTypes.includes(file.type)) {
      message.error('Only PNG, JPG, WebP, and GIF images are allowed.')
      return Upload.LIST_IGNORE
    }

    // 20MB limit
    if (file.size > 20 * 1024 * 1024) {
      message.error('Image must be under 20MB.')
      return Upload.LIST_IGNORE
    }

    if (newImages.length >= 10) {
      message.error('Maximum 10 images per update.')
      return Upload.LIST_IGNORE
    }

    const reader = new FileReader()
    reader.onload = e => {
      setNewImages(prev => [
        ...prev,
        {
          data: e.target.result,
          filename: file.name,
          content_type: file.type
        }
      ])
    }
    reader.readAsDataURL(file)
    return false
  }

  const handleRemoveNewImage = idx => {
    setNewImages(prev => prev.filter((_, i) => i !== idx))
  }

  // --- Submit ---
  const handleSave = async () => {
    if (!name.trim()) {
      message.error('Character name is required.')
      return
    }

    if (!character?.character_id) {
      message.error('Character ID is missing.')
      return
    }

    setIsSaving(true)

    try {
      // Build payload — only include changed fields
      const payload = {
        video_id: videoId || '',
        character_id: character.character_id
      }

      let hasChanges = false

      if (name.trim() !== (character.name || '')) {
        payload.name = name.trim()
        hasChanges = true
      }

      const currentGender = normalizeGender(character.gender)
      const selectedGender = normalizeGender(gender)
      if (selectedGender !== currentGender) {
        payload.gender = selectedGender
        hasChanges = true
      }

      const attrs = character.attributes || {}
      if (originalPrompt.trim() !== (attrs.original_prompt || '')) {
        payload.original_prompt = originalPrompt.trim()
        hasChanges = true
      }

      if (apparel.trim() !== (attrs.character_apparel || '')) {
        payload.character_apparel = apparel.trim()
        hasChanges = true
      }

      // Type change (super admin only)
      if (isSuperAdmin) {
        const currentIsSystem = character.type === 'system'
        if (isSystem !== currentIsSystem) {
          payload.type = isSystem ? 'system' : 'user'
          hasChanges = true
        }
      }

      // Images
      if (newImages.length > 0) {
        payload.images = newImages.map(img => ({
          data: img.data,
          filename: img.filename,
          content_type: img.content_type
        }))
        payload.replace_images = replaceImages
        hasChanges = true
      }

      // Check if anything changed
      if (!hasChanges) {
        message.info('No changes to save.')
        setIsSaving(false)
        return
      }

      await editCharacter(payload)

      message.success('Character updated successfully!')
      if (onUpdated) onUpdated()
      onClose()
    } catch (error) {
      console.error('Error updating character:', error)
      const errMsg =
        error?.message ||
        error?.response?.data?.error_msg ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Failed to update character. Please try again.'
      message.error(errMsg)
    } finally {
      setIsSaving(false)
    }
  }

  if (!character) return null

  const attrs = character.attributes || {}
  const currentPic = attrs.profile_pic || ''
  const existingImages = attrs.images || []

  // Collect existing images combining profile_pic and images array
  let allExistingImages = []
  if (existingImages.length > 0) {
    allExistingImages = [...existingImages]
    if (currentPic && !allExistingImages.includes(currentPic)) {
      allExistingImages.unshift(currentPic)
    }
  } else if (currentPic) {
    allExistingImages = [currentPic]
  }

  return (
    <Modal
      maskClosable={false}
      visible={visible}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon type="edit" /> Edit Character
        </div>
      }
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          onClick={handleSave}
        >
          Save Changes
        </Button>
      ]}
      width={700}
      centered
      className="edit-character-modal"
      closable={!isSaving}
      maskClosable={!isSaving}
    >
      {isSaving ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '60px 20px',
            gap: 16
          }}
        >
          <Spin size="large" />
          <h3>Updating Character...</h3>
          <p style={{ color: '#8c8c8c' }}>This may take a moment.</p>
        </div>
      ) : (
        <div className="edit-character-layout">
          {/* LEFT: Current image + upload */}
          <div className="edit-left-col">
            {/* Current profile pic */}
            <div className="current-image-box">
              {allExistingImages.length > 0 ? (
                <ImagePreview
                  src={allExistingImages[activeImageIndex] || allExistingImages[0]}
                  images={allExistingImages.length > 1 ? allExistingImages : undefined}
                  initialIndex={activeImageIndex}
                  alt={character.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div className="no-image-placeholder">
                  <Icon type="user" style={{ fontSize: 48, color: '#d9d9d9' }} />
                  <span>No image</span>
                </div>
              )}
            </div>

            {/* Existing images thumbnails */}
            {allExistingImages.length > 1 && (
              <div className="existing-images-thumbnails">
                {allExistingImages.slice(0, 4).map((img, idx) => {
                  const isLast = idx === 3;
                  const remaining = allExistingImages.length - 4;
                  return (
                    <div 
                      className={`thumbnail-item ${idx === activeImageIndex ? 'active' : ''}`} 
                      key={idx}
                      onClick={() => setActiveImageIndex(idx)}
                    >
                      <img
                        src={img}
                        alt={`${character.name}-${idx}`}
                      />
                      {isLast && remaining > 0 && (
                        <div className="more-overlay">
                          +{remaining}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Upload new images */}
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              multiple
              accept=".png,.jpg,.jpeg,.webp,.gif"
            >
              <Button icon="upload" style={{ width: '100%' }}>
                Add Reference Images
              </Button>
            </Upload>

            {/* New images preview */}
            {newImages.length > 0 && (
              <div className="new-images-list">
                {newImages.map((img, idx) => (
                  <div key={idx} className="new-image-item">
                    <img src={img.data} alt={img.filename} />
                    <Tooltip title="Remove">
                      <button
                        className="remove-new-image-btn"
                        onClick={() => handleRemoveNewImage(idx)}
                      >
                        <Icon type="close" />
                      </button>
                    </Tooltip>
                  </div>
                ))}
              </div>
            )}

            {/* Replace images toggle */}
            {newImages.length > 0 && (
              <Checkbox
                checked={replaceImages}
                onChange={e => setReplaceImages(e.target.checked)}
                style={{ marginTop: 8, fontSize: 12 }}
              >
                Replace all existing images
              </Checkbox>
            )}
          </div>

          {/* RIGHT: Form fields */}
          <div className="edit-right-col">
            <div className="edit-field">
              <label>Character Name <span style={{ color: '#ff4d4f' }}>*</span></label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Character name"
              />
            </div>

            <div className="edit-field">
              <label>Gender</label>
              <Select
                value={gender || undefined}
                onChange={val => setGender(normalizeGender(val))}
                style={{ width: '100%' }}
                allowClear
                placeholder="Select gender"
              >
                <Option value="male">Male</Option>
                <Option value="female">Female</Option>
                <Option value="unknown">Unknown</Option>
              </Select>
            </div>

            <div className="edit-field">
              <label>Description / Prompt</label>
              <TextArea
                value={originalPrompt}
                onChange={e => setOriginalPrompt(e.target.value)}
                placeholder="Describe the character's appearance..."
                rows={3}
              />
            </div>

            <div className="edit-field">
              <label>Apparel</label>
              <TextArea
                value={apparel}
                onChange={e => setApparel(e.target.value)}
                placeholder="e.g. leather jacket, blue jeans, sneakers"
                rows={2}
              />
              <span className="field-hint">Comma-separated list of clothing items</span>
            </div>

            {isSuperAdmin && (
              <div
                className={`system-toggle-card ${isSystem ? 'active' : ''}`}
                onClick={() => setIsSystem(!isSystem)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon type="global" />
                  <span>System Character</span>
                </div>
                <Checkbox checked={isSystem} />
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

export default EditCharacterModal
